import type { OperateWizardState, OperationKind, WizardSource, WizardStep } from './types';

/**
 * Per-mode step ordering. Step 0 (`'kind'`) is always the chooser. After the
 * operator picks a mode, the rest of the steps depend on what that mode
 * needs to produce a valid payload.
 *
 * `split_and_merge` is always 3 effective steps. When the merge target is
 * empty, the second-source picker is inlined into `sam_target` so the
 * operator never needs to navigate away to specify the absorbed FG.
 */
/**
 * Per-mode step ordering. `bioscope_movements` is inserted just before
 * `confirm` for the three composition modes that support it (split, merge,
 * split_and_merge). The step is filtered out at the modal layer when no
 * source cage has a bioscope or the operator lacks `canMoveBioscopes` —
 * keeping the indicator honest while letting the reducer remain context-free.
 * Redistribution is intentionally excluded from this iteration; bioscope
 * routing across N×M source/destination shapes is a follow-up.
 */
export const STEPS_BY_KIND: Record<OperationKind, readonly WizardStep[]> = {
    split: ['kind', 'split_continuation', 'split_targets', 'bioscope_movements', 'confirm'],
    merge: ['kind', 'merge_sources', 'merge_destination', 'bioscope_movements', 'confirm'],
    split_and_merge: ['kind', 'sam_continuation', 'sam_target', 'bioscope_movements', 'confirm'],
    redistribution: ['kind', 'redist_sources', 'redist_destinations', 'confirm'],
};

/**
 * The list of steps the wizard will walk through given the current mode.
 * Returns just `['kind']` when no mode has been chosen yet — the chooser
 * is the only step the operator can interact with at that point.
 */
export function getStepsForKind(kind: OperationKind | null): readonly WizardStep[] {
    if (kind === null) return ['kind'];
    return STEPS_BY_KIND[kind];
}

/**
 * Build the initial state pre-seeded with the fish group the operator opened
 * the wizard from and the location it lives in. The wizard always starts on
 * the kind chooser step.
 *
 * `operationDate` is passed in rather than read off the clock here so this
 * stays a pure function. The modal supplies today; omitting it yields the
 * "not chosen yet" state, which the confirm step reports as a required field.
 */
export function createInitialState(
    initialSource: WizardSource,
    locationId: number,
    operationDate: string | null = null
): OperateWizardState {
    return {
        step: 'kind',
        operationKind: null,
        locationId,
        initialSourceId: initialSource.fishGroupId,
        sources: [initialSource],
        destinations: [],
        operationDate,
        reason: '',
        notes: '',
        samTargetMode: null,
        bioscopeMovements: [],
    };
}
